/**
 * ActiviteTab — Embeds the tracker detail view for the affaire's activity feed
 * Instead of duplicating all tracker logic, we link to the full TrackerDetailPage
 * and provide a preview of recent activity here.
 */

import React, { useMemo, useState, useCallback } from 'react';
import { useAuth } from '@/core/auth';
import { ActivityFilters } from '@/features/admin/tabs/tracker/components/ActivityFilters';
import { ActivityFeed } from '@/features/admin/tabs/tracker/components/ActivityFeed';
import { InlineQuickComposer } from '@/features/admin/tabs/tracker/components/InlineQuickComposer';
import { EmailContentModal } from '@/features/admin/tabs/emails/components/EmailContentModal';
import { useChronologicCommunications } from '@/features/admin/tabs/tracker/hooks/useChronologicCommunications';
import { useTrackerEmails } from '@/features/admin/tabs/tracker/hooks/useTrackerEmails';
import { useJustifyingItems } from '@/features/admin/tabs/tracker/hooks/useJustifyingItems';
import { trackerAPI } from '@/features/admin/tabs/tracker/api/tracker.api';
import { mergeAndSortActivities } from '@/features/admin/tabs/tracker/utils/activityAdapters';
import type { CommunicationType, TrackerEmail } from '@/features/admin/tabs/tracker/types/tracker.types';
import type { Affaire } from '@/features/admin/tabs/search/types/search.types';
import type { ActivityItemViewModel } from '@/features/admin/tabs/tracker/components/ActivityItem/ActivityItem.types';

interface ActiviteTabProps {
  affaireId: number;
  affaire: Affaire;
}

const ALL_TYPES: CommunicationType[] = [
  'status_update', 'email', 'sms_outbox', 'sms_inbox',
  'com_avant_envoyer', 'com_post_it', 'com_internes', 'tracker_com', 'rdv',
  'voip_call'
];

export const ActiviteTab: React.FC<ActiviteTabProps> = ({ affaireId, affaire }) => {
  const { user } = useAuth();

  // Filter states
  const [enabledTypes, setEnabledTypes] = useState<Set<CommunicationType>>(new Set(ALL_TYPES));

  // Email modal
  const [viewEmailModal, setViewEmailModal] = useState<TrackerEmail | null>(null);

  // Edit tracker_com state
  const [editingTrackerCom, setEditingTrackerCom] = useState<{ id: string; content: string } | null>(null);

  // Fetch data using existing hooks
  const { communications, isLoading: communicationsLoading } = useChronologicCommunications(affaireId);
  const { data: emailsData, isLoading: emailsLoading } = useTrackerEmails(affaireId);
  const emails = emailsData?.data || [];
  const { highlightedComIds } = useJustifyingItems(affaireId);

  // Build activity view models
  const activities = useMemo<ActivityItemViewModel[]>(() => {
    const baseActivities = mergeAndSortActivities(communications, emails, 'desc');

    // Add synthetic RDV card if date_du_rv exists
    if (affaire?.date_du_rv) {
      try {
        const [year, month, day] = affaire.date_du_rv.split('-').map(Number);
        const [hours, minutes] = (affaire.heure_rv || '09:00').split(':').map(Number);
        const rdvDate = new Date(year, month - 1, day, hours || 9, minutes || 0);

        if (!isNaN(rdvDate.getTime())) {
          const rdvActivity: ActivityItemViewModel = {
            type: 'rdv',
            id: `rdv-${affaire.id_unique}`,
            title: 'Rendez-vous expertise',
            meta: affaire.expert ? `Expert: ${affaire.expert}` : undefined,
            snippet: affaire.address
              ? `${affaire.address.number || ''} ${affaire.address.street || ''}, ${affaire.address.postcode || ''} ${affaire.address.locality || ''}`.trim().replace(/\s+/g, ' ')
              : undefined,
            timestamp: rdvDate,
            urgency: null,
            isDone: false,
          };
          const all = [...baseActivities, rdvActivity];
          all.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
          return all;
        }
      } catch { /* ignore invalid dates */ }
    }

    return baseActivities;
  }, [communications, emails, affaire]);

  // Type counts for filter badges
  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    ALL_TYPES.forEach(t => { counts[t] = 0; });
    counts.sms = 0;
    activities.forEach(a => {
      if (a.type in counts) counts[a.type]++;
      if (a.type === 'sms_outbox' || a.type === 'sms_inbox') counts.sms++;
    });
    return counts;
  }, [activities]);

  // Handlers
  const handleToggleType = useCallback((type: CommunicationType) => {
    setEnabledTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);

  const handleViewEmail = useCallback((emailId: string) => {
    const email = emails.find(e => e.id === emailId);
    if (email) setViewEmailModal(email);
  }, [emails]);

  const handleMarkDone = useCallback(async (type: string, id: string, isDone: boolean) => {
    try {
      await trackerAPI.markCommunicationDone(type, id, isDone);
    } catch { /* ignore */ }
  }, []);

  const handleNoteCreated = useCallback(() => {
    // Queries auto-refetch via React Query
  }, []);

  const handleEditTrackerCom = useCallback(async (id: string, content: string) => {
    try {
      await trackerAPI.updateTrackerCom(id, content);
      setEditingTrackerCom(null);
    } catch { /* ignore */ }
  }, []);

  const handleDeleteTrackerCom = useCallback(async (id: string) => {
    try {
      await trackerAPI.deleteTrackerCom(id);
    } catch { /* ignore */ }
  }, []);

  const isLoading = communicationsLoading || emailsLoading;

  return (
    <div className="aff-detail-activite">
      {/* Quick Composer */}
      <div className="aff-detail-activite-composer">
        <InlineQuickComposer
          affaireId={affaireId}
          onNoteCreated={handleNoteCreated}
        />
      </div>

      {/* Filters */}
      <ActivityFilters
        enabledTypes={enabledTypes}
        typeCounts={typeCounts as Record<CommunicationType | 'sms', number>}
        onToggleType={handleToggleType}
        onShowAll={() => setEnabledTypes(new Set(ALL_TYPES))}
      />

      {/* Activity Feed */}
      {isLoading ? (
        <div className="aff-detail-loading" style={{ height: 200 }}>
          <div className="aff-detail-spinner" />
          <span>Chargement de l'activité...</span>
        </div>
      ) : (
        <ActivityFeed
          activities={activities}
          enabledTypes={enabledTypes}
          highlightedIds={highlightedComIds}
          currentUserId={user?.id}
          onMarkDone={handleMarkDone}
          onEdit={(id, content) => handleEditTrackerCom(id, content)}
          onDelete={handleDeleteTrackerCom}
          onViewEmailFull={handleViewEmail}
        />
      )}

      {/* Email Content Modal */}
      {viewEmailModal && (
        <EmailContentModal
          email={viewEmailModal}
          onClose={() => setViewEmailModal(null)}
        />
      )}
    </div>
  );
};
